import React, { useState } from 'react';
import { Phone, Mail, MapPin, Send } from 'lucide-react';
import './Contact.css';

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    eventType: '',
    guests: '',
    eventDate: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitStatus('success');
      setFormData({
        name: '',
        phone: '',
        email: '',
        eventType: '',
        guests: '',
        eventDate: '',
        message: ''
      });
      
      setTimeout(() => {
        setSubmitStatus(null);
      }, 5000);
    }, 1000);
  };

  return (
    <div className="contact-page">
      <section className="contact-hero">
        <div className="container">
          <span className="section-subtitle text-gold">Get in Touch</span>
          <h1 className="hero-medium">Contact Us</h1>
          <p className="body-large mt-24">
            Let's discuss your celebration and create something extraordinary together
          </p>
        </div>
      </section>

      <section className="section-padding">
        <div className="container">
          <div className="contact-grid">
            {/* Contact Information */}
            <div className="contact-info-section">
              <h2 className="heading-2 mb-32">Reach Out to Us</h2>
              
              <div className="contact-methods">
                <div className="contact-method-card">
                  <div className="method-icon">
                    <Phone size={24} />
                  </div>
                  <div>
                    <h3 className="heading-3 mb-8">Phone</h3>
                    <a href="tel:+918826372788" className="contact-link">+91 8826372788</a>
                    <p className="body-small mt-8">Available 9 AM - 9 PM daily</p>
                  </div>
                </div>

                <div className="contact-method-card">
                  <div className="method-icon">
                    <Mail size={24} />
                  </div>
                  <div>
                    <h3 className="heading-3 mb-8">Email</h3>
                    <a href="mailto:sales@greenvalleyagra.com" className="contact-link">sales@greenvalleyagra.com</a>
                    <p className="body-small mt-8">We'll respond within 24 hours</p>
                  </div>
                </div>

                <div className="contact-method-card">
                  <div className="method-icon">
                    <MapPin size={24} />
                  </div>
                  <div>
                    <h3 className="heading-3 mb-8">Visit Us</h3>
                    <p className="body-regular">100 Ft Road, Near Krishna Indraprastha Colony<br />
                    Shastripuram, Agra – 282007</p>
                    <a href="https://www.google.com/maps/search/?api=1&query=The+Green+Valley+Shastripuram+Agra" 
                       target="_blank" 
                       rel="noopener noreferrer" 
                       className="btn-secondary mt-16">
                      Get Directions
                    </a>
                  </div>
                </div>
              </div>

              <div className="whatsapp-banner">
                <h3 className="heading-3 mb-16">Prefer WhatsApp?</h3>
                <p className="body-regular mb-24">
                  Get instant responses to your queries. Chat with our team on WhatsApp.
                </p>
                <a href="https://wa.me/918826372788?text=Hi!%20I'd%20like%20to%20enquire%20about%20The%20Green%20Valley." 
                   target="_blank" 
                   rel="noopener noreferrer" 
                   className="btn-primary">
                  WhatsApp Us Now
                </a>
              </div>
            </div>

            {/* Contact Form */}
            <div className="contact-form-section">
              <div className="form-container">
                <h2 className="heading-2 mb-24">Send Us a Message</h2>
                <form onSubmit={handleSubmit} className="contact-form">
                  <div className="form-group">
                    <label htmlFor="name" className="form-label">Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="form-input"
                      placeholder="Your full name"
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="phone" className="form-label">Phone *</label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="form-input"
                        placeholder="+91 XXXXX XXXXX"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="email" className="form-label">Email</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="eventType" className="form-label">Event Type</label>
                    <select
                      id="eventType"
                      name="eventType"
                      value={formData.eventType}
                      onChange={handleChange}
                      className="form-input"
                    >
                      <option value="">Select event type</option>
                      <option value="wedding">Wedding</option>
                      <option value="reception">Reception</option>
                      <option value="engagement">Engagement</option>
                      <option value="haldi-mehndi">Haldi & Mehndi</option>
                      <option value="anniversary">Anniversary</option>
                      <option value="birthday">Birthday</option>
                      <option value="corporate">Corporate Event</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="guests" className="form-label">Expected Guests</label>
                      <input
                        type="text"
                        id="guests"
                        name="guests"
                        value={formData.guests}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="Approx. number"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="eventDate" className="form-label">Preferred Date</label>
                      <input
                        type="date"
                        id="eventDate"
                        name="eventDate"
                        value={formData.eventDate}
                        onChange={handleChange}
                        className="form-input"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="message" className="form-label">Message</label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows="5"
                      className="form-input"
                      placeholder="Tell us about your event..."
                    ></textarea>
                  </div>

                  <button type="submit" className="btn-primary submit-button" disabled={isSubmitting}>
                    {isSubmitting ? 'Sending...' : (
                      <>
                        Send Message
                        <Send size={20} style={{ marginLeft: '8px' }} />
                      </>
                    )}
                  </button>

                  {submitStatus === 'success' && (
                    <div className="success-message">
                      Thank you! We've received your message and will get back to you within 24 hours.
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="map-section">
        <div className="container">
          <h2 className="heading-1 text-center mb-48">Find Us Here</h2>
          <div className="map-container">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3548.234!2d77.97625!3d27.17083!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjfCsDEwJzE1LjAiTiA3N8KwNTgnMzQuNSJF!5e0!3m2!1sen!2sin!4v1234567890"
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="The Green Valley Location"
            ></iframe>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section-padding final-cta">
        <div className="container">
          <div className="glass-card cta-card">
            <h2 className="hero-medium text-center">Contact Us Now</h2>
            <p className="body-xl text-center mt-24 mb-32">
              Ready to plan your dream celebration? Get in touch with our team today!
            </p>
            <div className="cta-buttons">
              <a href="https://wa.me/918826372788?text=Hi!%20I'd%20like%20to%20enquire%20about%20The%20Green%20Valley." 
                 target="_blank" 
                 rel="noopener noreferrer" 
                 className="btn-primary">
                WhatsApp Us
              </a>
              <a href="tel:+918826372788" className="btn-secondary">
                Call Now
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
